import React from 'react';
import { ConnectorPosition, useArrowStore } from '@/stores/arrowStore';
import { cn } from '@/lib/utils';

interface ConnectorPointsProps {
  itemId: string;
  x: number;
  y: number;
  width: number;
  height: number;
  zoom: number;
  onConnectorMouseDown: (position: ConnectorPosition, point: { x: number; y: number }) => void;
}

export function ConnectorPoints({ 
  itemId, 
  x, 
  y, 
  width, 
  height, 
  zoom,
  onConnectorMouseDown 
}: ConnectorPointsProps) {
  const { isArrowMode, hoveredConnector, setHoveredConnector } = useArrowStore();
  
  if (!isArrowMode) return null;
  
  const connectors: { position: ConnectorPosition; cx: number; cy: number }[] = [
    { position: 'top', cx: x + width / 2, cy: y },
    { position: 'bottom', cx: x + width / 2, cy: y + height },
    { position: 'left', cx: x, cy: y + height / 2 },
    { position: 'right', cx: x + width, cy: y + height / 2 },
  ];
  
  const isHovered = (position: ConnectorPosition) => 
    hoveredConnector?.itemId === itemId && hoveredConnector?.position === position;
  
  // Size scales inversely with zoom to maintain consistent visual size
  const baseSize = 3 / zoom;
  const hoverSize = 5 / zoom;
  
  return (
    <>
      {connectors.map(({ position, cx, cy }) => (
        <g key={position}>
          {/* Invisible larger hit area */}
          <circle
            cx={cx}
            cy={cy}
            r={20 / zoom}
            fill="transparent"
            style={{ pointerEvents: 'auto' }}
            className="cursor-crosshair"
            onMouseEnter={() => setHoveredConnector({ itemId, position })}
            onMouseLeave={() => setHoveredConnector(null)}
            onMouseDown={(e) => {
              e.stopPropagation();
              onConnectorMouseDown(position, { x: cx, y: cy });
            }}
          />
          
          {/* Visible connector point */}
          <circle
            cx={cx}
            cy={cy}
            r={isHovered(position) ? hoverSize : baseSize}
            fill={isHovered(position) ? 'hsl(var(--primary))' : 'hsl(var(--background))'}
            stroke="hsl(var(--primary))"
            strokeWidth={1.5 / zoom}
            className={cn(
              'transition-all duration-150 pointer-events-none',
              isHovered(position) && 'animate-pulse'
            )}
          />
        </g>
      ))}
    </>
  );
}
